export default {
  en: {
    title: 'Extensions',
    subtitle: 'Embed iframes or web components inside the agent console. Once created, you can attach an extension from a segment or calendar.',
    columns: {
      name: 'Name',
      kind: 'Type',
      placement: 'Placement',
      target: 'Target',
      enabled: 'Status',
    },
    kinds: {
      iframe: 'Iframe',
      'web-component': 'Web component',
    },
    placements: {
      'call-panel': 'Call panel',
      sidebar: 'Sidebar',
      standalone: 'Standalone',
    },
    contextScopes: {
      user: 'Agent',
      segment: 'Segment',
      call: 'Call',
      contact: 'Contact',
    },
    attach: {
      placeholder: 'Select extensions to attach',
      segment: 'Extensions enabled when an agent works on this segment.',
      service: 'Extensions enabled when an agent attends a call from this calendar.',
    },
    form: {
      name: {
        placeholder: 'CRM lookup',
        hint: 'Name visible to your agents in the console.',
        error: 'Please enter a name for the extension.',
        errorMax: 'The name cannot exceed 80 characters.',
      },
      description: {
        placeholder: 'What this extension does.',
        hint: 'Optional description shown to your agents.',
        errorMax: 'The description cannot exceed 500 characters.',
      },
      kind: {
        label: 'Type',
        hint: 'Choose how the extension will be embedded in the console.',
        error: 'Please choose a type.',
      },
      placement: {
        label: 'Placement',
        hint: 'Where the extension is rendered in the console.',
        error: 'Please choose a placement.',
      },
      icon: {
        label: 'Icon',
        placeholder: 'appstore',
        hint: 'Ant Design icon name (v1) used when no icon URL is provided.',
        errorMax: 'The icon name cannot exceed 80 characters.',
      },
      iconUrl: {
        label: 'Icon URL',
        placeholder: 'https://cdn.example.com/icon.svg',
        hint: 'HTTPS URL to a custom icon image. Takes precedence over the icon name.',
        error: 'The icon URL must start with https://.',
        errorMax: 'The icon URL cannot exceed 2000 characters.',
      },
      kindSettings: {
        iframe: 'Iframe settings',
        webComponent: 'Web component settings',
      },
      iframe: {
        url: {
          label: 'URL',
          hint: 'HTTPS URL of the page to embed inside the iframe.',
          error: 'The URL is required for iframe extensions.',
          errorPattern: 'The URL must start with https://.',
        },
        allow: {
          label: 'Allow attributes',
          placeholder: 'camera, microphone, ...',
          hint: 'Permissions exposed via the iframe `allow` attribute.',
        },
        sandbox: {
          label: 'Sandbox attributes',
          placeholder: 'allow-scripts, allow-same-origin, ...',
          hint: 'Restrictions applied via the iframe `sandbox` attribute.',
        },
        width: {
          label: 'Sidebar width (%)',
          hint: 'Width as a percentage of the viewport (35-100) used when the extension opens in the sidebar.',
          error: 'The width must be a number between 35 and 100.',
        },
      },
      webComponent: {
        scriptUrl: {
          label: 'Script URL',
          hint: 'HTTPS URL of the JS module that defines the custom element.',
          error: 'The script URL is required for web component extensions.',
          errorPattern: 'The script URL must start with https://.',
        },
        tagName: {
          label: 'Tag name',
          hint: 'Custom element tag name (lowercase, must contain a dash).',
          error: 'The tag name is required for web component extensions.',
          errorPattern: 'The tag name must be lowercase and contain a dash, e.g. my-extension.',
        },
        integrity: {
          label: 'Integrity (SRI)',
          hint: 'Optional Subresource Integrity hash for the script.',
        },
      },
      advanced: 'Advanced',
      contextScopes: {
        label: 'Context scopes',
        placeholder: 'Select context values',
        hint: 'Which host context values the extension receives on init.',
      },
      enabled: {
        label: 'Enabled',
        hint: 'Disable to keep the extension hidden from the console.',
      },
    },
  },
  es: {
    title: 'Extensiones',
    subtitle: 'Incrusta iframes o web components dentro de la consola de agentes. Una vez creadas, podrás asociar una extensión desde un segmento o calendario.',
    columns: {
      name: 'Nombre',
      kind: 'Tipo',
      placement: 'Ubicación',
      target: 'Destino',
      enabled: 'Estado',
    },
    kinds: {
      iframe: 'Iframe',
      'web-component': 'Web component',
    },
    placements: {
      'call-panel': 'Panel de llamada',
      sidebar: 'Panel lateral',
      standalone: 'Pantalla completa',
    },
    contextScopes: {
      user: 'Agente',
      segment: 'Segmento',
      call: 'Llamada',
      contact: 'Contacto',
    },
    attach: {
      placeholder: 'Selecciona las extensiones a asociar',
      segment: 'Extensiones disponibles cuando un agente atiende este segmento.',
      service: 'Extensiones disponibles cuando un agente atiende una llamada de este calendario.',
    },
    form: {
      name: {
        placeholder: 'Búsqueda en CRM',
        hint: 'Nombre visible para tus agentes en la consola.',
        error: 'Por favor ingresa un nombre para la extensión.',
        errorMax: 'El nombre no puede superar los 80 caracteres.',
      },
      description: {
        placeholder: 'Para qué sirve esta extensión.',
        hint: 'Descripción opcional visible para tus agentes.',
        errorMax: 'La descripción no puede superar los 500 caracteres.',
      },
      kind: {
        label: 'Tipo',
        hint: 'Elige cómo se incrustará la extensión en la consola.',
        error: 'Selecciona un tipo.',
      },
      placement: {
        label: 'Ubicación',
        hint: 'Dónde se renderiza la extensión dentro de la consola.',
        error: 'Selecciona una ubicación.',
      },
      icon: {
        label: 'Ícono',
        placeholder: 'appstore',
        hint: 'Nombre del ícono de Ant Design (v1) que se usará si no defines una URL de ícono.',
        errorMax: 'El nombre del ícono no puede superar los 80 caracteres.',
      },
      iconUrl: {
        label: 'URL del ícono',
        placeholder: 'https://cdn.example.com/icon.svg',
        hint: 'URL HTTPS de una imagen personalizada. Tiene prioridad sobre el nombre del ícono.',
        error: 'La URL del ícono debe comenzar con https://.',
        errorMax: 'La URL del ícono no puede superar los 2000 caracteres.',
      },
      kindSettings: {
        iframe: 'Configuración del iframe',
        webComponent: 'Configuración del web component',
      },
      iframe: {
        url: {
          label: 'URL',
          hint: 'URL HTTPS de la página que se incrustará en el iframe.',
          error: 'La URL es obligatoria para las extensiones de tipo iframe.',
          errorPattern: 'La URL debe comenzar con https://.',
        },
        allow: {
          label: 'Atributos allow',
          placeholder: 'camera, microphone, ...',
          hint: 'Permisos expuestos mediante el atributo `allow` del iframe.',
        },
        sandbox: {
          label: 'Atributos sandbox',
          placeholder: 'allow-scripts, allow-same-origin, ...',
          hint: 'Restricciones aplicadas mediante el atributo `sandbox` del iframe.',
        },
        width: {
          label: 'Ancho del sidebar (%)',
          hint: 'Ancho como porcentaje de la ventana (35-100) cuando se abre la extensión en el panel lateral.',
          error: 'El ancho debe ser un número entre 35 y 100.',
        },
      },
      webComponent: {
        scriptUrl: {
          label: 'URL del script',
          hint: 'URL HTTPS del módulo JS que define el custom element.',
          error: 'La URL del script es obligatoria para extensiones web component.',
          errorPattern: 'La URL del script debe comenzar con https://.',
        },
        tagName: {
          label: 'Nombre de la etiqueta',
          hint: 'Nombre del custom element (en minúsculas, debe contener un guión).',
          error: 'El nombre de la etiqueta es obligatorio para extensiones web component.',
          errorPattern: 'El nombre debe estar en minúsculas y contener un guión, por ejemplo: my-extension.',
        },
        integrity: {
          label: 'Integrity (SRI)',
          hint: 'Hash opcional de Subresource Integrity para el script.',
        },
      },
      advanced: 'Avanzado',
      contextScopes: {
        label: 'Contextos disponibles',
        placeholder: 'Selecciona los valores de contexto',
        hint: 'Qué valores de contexto recibirá la extensión al iniciarse.',
      },
      enabled: {
        label: 'Activada',
        hint: 'Desactívala para ocultarla en la consola sin eliminarla.',
      },
    },
  },
  fr: {
    title: 'Extensions',
    subtitle: 'Intégrez des iframes ou des web components dans la console agent. Une fois créées, vous pouvez associer une extension à un segment ou un calendrier.',
    columns: {
      name: 'Nom',
      kind: 'Type',
      placement: 'Emplacement',
      target: 'Cible',
      enabled: 'Statut',
    },
    kinds: {
      iframe: 'Iframe',
      'web-component': 'Web component',
    },
    placements: {
      'call-panel': 'Panneau d\'appel',
      sidebar: 'Panneau latéral',
      standalone: 'Plein écran',
    },
    contextScopes: {
      user: 'Agent',
      segment: 'Segment',
      call: 'Appel',
      contact: 'Contact',
    },
    attach: {
      placeholder: 'Sélectionnez les extensions à associer',
      segment: 'Extensions disponibles lorsque l\'agent travaille sur ce segment.',
      service: 'Extensions disponibles lorsque l\'agent gère un appel depuis ce calendrier.',
    },
    form: {
      name: {
        placeholder: 'Recherche CRM',
        hint: 'Nom visible par vos agents dans la console.',
        error: 'Veuillez saisir un nom pour l\'extension.',
        errorMax: 'Le nom ne peut pas dépasser 80 caractères.',
      },
      description: {
        placeholder: 'À quoi sert cette extension.',
        hint: 'Description facultative visible par vos agents.',
        errorMax: 'La description ne peut pas dépasser 500 caractères.',
      },
      kind: {
        label: 'Type',
        hint: 'Choisissez comment l\'extension sera intégrée dans la console.',
        error: 'Veuillez choisir un type.',
      },
      placement: {
        label: 'Emplacement',
        hint: 'Où l\'extension est affichée dans la console.',
        error: 'Veuillez choisir un emplacement.',
      },
      icon: {
        label: 'Icône',
        placeholder: 'appstore',
        hint: 'Nom de l\'icône Ant Design (v1) utilisée si aucune URL d\'icône n\'est fournie.',
        errorMax: 'Le nom de l\'icône ne peut pas dépasser 80 caractères.',
      },
      iconUrl: {
        label: 'URL de l\'icône',
        placeholder: 'https://cdn.example.com/icon.svg',
        hint: 'URL HTTPS d\'une image personnalisée. Elle a priorité sur le nom de l\'icône.',
        error: 'L\'URL de l\'icône doit commencer par https://.',
        errorMax: 'L\'URL de l\'icône ne peut pas dépasser 2000 caractères.',
      },
      kindSettings: {
        iframe: 'Paramètres de l\'iframe',
        webComponent: 'Paramètres du web component',
      },
      iframe: {
        url: {
          label: 'URL',
          hint: 'URL HTTPS de la page à intégrer dans l\'iframe.',
          error: 'L\'URL est obligatoire pour les extensions de type iframe.',
          errorPattern: 'L\'URL doit commencer par https://.',
        },
        allow: {
          label: 'Attributs allow',
          placeholder: 'camera, microphone, ...',
          hint: 'Permissions exposées via l\'attribut `allow` de l\'iframe.',
        },
        sandbox: {
          label: 'Attributs sandbox',
          placeholder: 'allow-scripts, allow-same-origin, ...',
          hint: 'Restrictions appliquées via l\'attribut `sandbox` de l\'iframe.',
        },
        width: {
          label: 'Largeur du panneau (%)',
          hint: 'Largeur en pourcentage de la fenêtre (35-100) lorsque l\'extension est ouverte dans le panneau latéral.',
          error: 'La largeur doit être un nombre entre 35 et 100.',
        },
      },
      webComponent: {
        scriptUrl: {
          label: 'URL du script',
          hint: 'URL HTTPS du module JS qui définit le custom element.',
          error: 'L\'URL du script est obligatoire pour les extensions web component.',
          errorPattern: 'L\'URL du script doit commencer par https://.',
        },
        tagName: {
          label: 'Nom de la balise',
          hint: 'Nom du custom element (en minuscules, doit contenir un tiret).',
          error: 'Le nom de la balise est obligatoire pour les extensions web component.',
          errorPattern: 'Le nom doit être en minuscules et contenir un tiret, par exemple my-extension.',
        },
        integrity: {
          label: 'Integrity (SRI)',
          hint: 'Hash Subresource Integrity facultatif pour le script.',
        },
      },
      advanced: 'Avancé',
      contextScopes: {
        label: 'Contextes disponibles',
        placeholder: 'Sélectionnez les valeurs de contexte',
        hint: 'Quelles valeurs de contexte l\'extension recevra à l\'initialisation.',
      },
      enabled: {
        label: 'Activée',
        hint: 'Désactivez pour masquer l\'extension de la console sans la supprimer.',
      },
    },
  },
};
